<template>
	<div class="landing-age-grid" :class="`landing-age-grid_theme_${theme}`">
		<div class="landing-age-grid__grid">
			<div
				class="landing-age-grid__item"
				v-for="ageRange in ageRanges"
				:key="ageRange.value"
				:class="{ active: ageRange.value === activeValue }"
				@click="onClick(ageRange)"
				data-control="button"
				data-button-type="age"
				:data-value="ageRange.value"
			>
				<img class="landing-age-grid__image" :src="ageRange.img" alt="" />
				<div class="landing-age-grid__item-top"></div>
				<div class="landing-age-grid__item-bottom">
					<div class="landing-age-grid__item-text">{{ ageRange.label }}</div>
					<div class="landing-age-grid__item-icon">
						<svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
							<circle cx="12.1016" cy="12" r="10" fill="white" />
							<path
								d="M16.0736 12.0001C16.0736 12.4801 15.8836 12.9301 15.5436 13.2701L11.1736 17.6501C10.7036 18.1201 9.94359 18.1201 9.47359 17.6501C9.00359 17.1801 9.00359 16.4201 9.47359 15.9501L13.2136 12.2101C13.3336 12.0901 13.3336 11.9001 13.2136 11.7901L9.47359 8.05006C9.00359 7.58006 9.00359 6.82006 9.47359 6.35006C9.94359 5.88006 10.7036 5.88006 11.1736 6.35006L15.5436 10.7301C15.8836 11.0701 16.0736 11.5201 16.0736 12.0001Z"
							/>
						</svg>
					</div>
				</div>
			</div>
		</div>
		<div v-if="otherAgesButton" class="landing-age-grid__other-ages">
			<button
				@click="onClick(otherAgesButton.data)"
				class="landing-age-grid__other-ages-button btn"
				type="button"
				data-control="button"
				data-button-type="age"
				:data-value="otherAgesButton.data.value"
			>
				<span>{{ otherAgesButton.text }}</span>
				<i class="icon-arrow-right"></i>
			</button>
		</div>
	</div>
</template>

<script>
export default {
	name: 'LandingAgeGrid',

	props: {
		ageRanges: {
			type: Array,
			required: true,
		},

		otherAgesButton: {
			type: Object,
			default: null,
		},

		theme: {
			type: String,
			required: true,
			validator: (value) => ['blue', 'purple', 'green', 'military', 'military-gs'].includes(value),
		},
	},

	emits: ['ageSelected'],

	data() {
		return {
			clicked: false,
			activeValue: null,
		}
	},

	methods: {
		onClick(ageRange) {
			if (this.clicked) {
				return
			}
			this.clicked = true
			this.activeValue = ageRange.value

			setTimeout(() => {
				this.$emit('ageSelected', ageRange)
				this.activeValue = null
				this.clicked = false
			}, 300)
		},
	},
}
</script>

<style lang="scss" scoped>
.landing-age-grid {
	&_theme {
		&_blue {
			--landing-age-grid-bg: linear-gradient(90deg, #586bff 6.87%, #8997ff 90.24%);
			--landing-age-grid-icon-fill: #98a4ff;
		}

		&_purple {
			--landing-age-grid-bg: linear-gradient(90deg, #6471ff 6.87%, #d286ff 90.24%);
			--landing-age-grid-icon-fill: #d387ff;
		}

		&_green {
			--landing-age-grid-bg: #528c6b;
			--landing-age-grid-icon-fill: #528c6b;

			.landing-age-grid__item-top {
				background: #dfe8df;
			}
		}

		&_military,
		&_military-gs {
			--landing-age-grid-bg: #6a8865;
			--landing-age-grid-icon-fill: #6a8865;

			.landing-age-grid__item-top {
				background: var(--Fill-Default-Full);
			}
		}

		// GS funnel only: taller cards + full-bleed couples (no height clamp)
		&_military-gs {
			.landing-age-grid__item {
				padding-bottom: 118%;
			}

			.landing-age-grid__image {
				height: auto;
				max-height: none;
				bottom: 2.5rem;
			}
		}
	}

	&__grid {
		display: grid;
		grid-template-columns: repeat(2, 1fr);
		gap: 0.5rem 0.75rem;
	}

	&__item {
		height: 0;
		padding-bottom: 107%;
		position: relative;
		overflow: hidden;
		border-radius: 0 0 1rem 1rem;
		cursor: pointer;

		&.active {
			animation: click 0.2s ease-in-out;
		}
	}

	&__item-bottom {
		position: absolute;
		bottom: 0;
		left: 0;
		right: 0;
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: 0.5rem 0.7rem;
		background: var(--landing-age-grid-bg);
		font-weight: 600;
		line-height: 1.25;
		font-size: 1rem;
		color: #fff;
		z-index: 2;
	}

	&__item-icon {
		svg {
			path {
				fill: var(--landing-age-grid-icon-fill);
			}
		}
	}

	&__item-top {
		position: absolute;
		top: 23%;
		left: 0;
		height: 87%;
		width: 100%;
		background: var(--Fill-Accent-16);
		border-radius: 47.6% 50.5% 0 0;
		z-index: 0;
	}

	&__image {
		position: absolute;
		pointer-events: none;
		z-index: 1;
		width: 100%;
		max-height: 140px;
		height: 9.7rem;
		object-fit: contain;
		left: 0;
		bottom: calc(1rem * 1.25 + 0.5rem * 2 + 0.25rem);
	}

	&__other-ages-button {
		font-size: var(--size-16);
		font-weight: 600;
		padding: 0;
		margin: 0;
	}

	&__other-ages {
		display: flex;
		justify-content: center;
		margin-top: var(--size-24);

		i {
			font-size: var(--size-12);
			margin-left: var(--size-6);
		}
	}
}

@keyframes click {
	0% {
		transform: translate(0, 0);
	}
	50% {
		transform: translate(0px, 3px);
	}
	100% {
		transform: translate(0, 0);
	}
}
</style>
